import { defineStore } from 'pinia';
import { computed, ComputedRef, ref } from 'vue';
import { DashboardData, DashboardOption } from '@/dashboard/shared/types/types';

import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { getV8DashboardsWidgets } from '@/api-client/metav8Over/queries/dashboard-widget-v8-controller/dashboard-widget-v8-controller';
import { WidgetV8MappingResponseItem } from '@/api-client/metav8Over/model';
import {
  getV8Dashboards,
  postV8DashboardsDashboardVisitByDashboardId,
} from '@/api-client/metav8Over/queries/integrated-dashboard-v8-controller/integrated-dashboard-v8-controller';
import { convertToDashboardData } from '@/dashboard/features/dashboardCore/services/dashboard';
import { getDefaultDashboardData } from '@/dashboard/features/dashboardCore/constants/dashboard.data';
import WorkerManager from '@/worker/WorkerManager';
import { abortApiController, makeApiKey } from '@/common/utils/abortApi.utils';
import { convertDashboardWidgetsCoordinates } from '@/dashboard/features/widgets/common/domain/widgetCoordinates';
import { ABORT_API_KEYS } from '@/dashboard/features/widgets/common/constants/define';
type ApiCallId = (typeof ABORT_API_KEYS)[keyof typeof ABORT_API_KEYS];

const useAbortDashboardApi = (dashboardId: ComputedRef<DashboardData['dashboardId']>) => {
  const abortManager = abortApiController;

  const getApiKey = (callId: ApiCallId = 'default') => {
    return makeApiKey({ categoryId: `${dashboardId.value}`, callId });
  };

  const getAbortSignal = (callId: ApiCallId) => {
    const apiKey = getApiKey(callId);
    abortManager.setMap(apiKey);
    return abortManager.getSignal(apiKey);
  };

  const abortApiCall = (callIds: ApiCallId[]) => {
    if (!Array.isArray(callIds)) {
      return;
    }
    const apiKeys = callIds.map((id) => getApiKey(id));
    abortManager.abortCallsByCategory(apiKeys);
  };

  return {
    getAbortSignal,
    abortApiCall,
    abortAll: abortManager.abortAll,
    abortOtherCategories: abortManager.abortOtherCategories,
  };
};

const useControlDashboardApi = (dashboardId: ComputedRef<DashboardData['dashboardId']>) => {
  const dashboardApiController = useAbortDashboardApi(dashboardId);
  const workerManager = new WorkerManager();

  const changeIdByRouter = (id: 'all' | DashboardData['dashboardId'] | string) => {
    if (id === 'all') {
      workerManager.abortAllApi();
      dashboardApiController.abortAll();
    } else {
      const apiKey = makeApiKey({ categoryId: `${id}` });

      workerManager.abortOtherCategories(apiKey);
      dashboardApiController.abortOtherCategories(apiKey);
    }
  };

  return {
    changeIdByRouter,
    dashboardApiController,
  };
};

export const useDashboardViewStore = defineStore('dashboardDashboardView', () => {
  const monitoringDashboard = ref<DashboardData>(getDefaultDashboardData());

  const isVariableFilterDisabled = computed(
    () => monitoringDashboard.value.dashboardOption.useVariableFilter === false,
  );

  const toggleShowTimePeriod = () => {
    monitoringDashboard.value.dashboardOption.showTimePeriod =
      !monitoringDashboard.value.dashboardOption.showTimePeriod;
  };

  // Period Range
  const setGlobalPeriodRange = (value: DashboardOption['periodRange']) => {
    monitoringDashboard.value.dashboardOption.periodRange = value ?? 'none';
  };

  // Default Period Range
  const setGlobalDefaultPeriodRange = (value: DashboardOption['defaultPeriodRange']) => {
    monitoringDashboard.value.dashboardOption.defaultPeriodRange = value ?? 'p10m';
  };

  // Screen Refresh Interval
  const globalPeriodInterval = computed(
    () => monitoringDashboard.value.dashboardOption.periodInterval ?? 'I5s',
  );
  const setGlobalPeriodInterval = (_interval: DashboardOption['periodInterval']) => {
    monitoringDashboard.value.dashboardOption.periodInterval = _interval;
  };

  // Tick Setting
  const globalTickSetting = computed(
    () => monitoringDashboard.value.dashboardOption.tickSetting ?? 'I5s',
  );
  const setGlobalTickSetting = (tickSetting: DashboardOption['tickSetting']) => {
    monitoringDashboard.value.dashboardOption.tickSetting = tickSetting;
  };

  // Alert Activation
  const globalAlertActivation = computed(
    () => monitoringDashboard.value.dashboardOption.alertActivation ?? true,
  );
  const setGlobalAlertActivation = (alertActivation: DashboardOption['alertActivation']) => {
    monitoringDashboard.value.dashboardOption.alertActivation = alertActivation;
  };

  // Only Target Name
  const globalOnlyTargetName = computed(
    () => monitoringDashboard.value.dashboardOption.onlyTargetName ?? true,
  );
  const setGlobalOnlyTargetName = (onlyTargetName: DashboardOption['onlyTargetName']) => {
    monitoringDashboard.value.dashboardOption.onlyTargetName = onlyTargetName;
  };

  const widgetsForMonitoringDashboard = ref<WidgetV8MappingResponseItem>();

  const setMonitoringDashboard = (data: DashboardData) => {
    monitoringDashboard.value = data;
  };

  const fetchCurrentDashboardInfo = async (dashboardId: number) => {
    try {
      const { data } = await getV8Dashboards(
        {
          dashboardId,
          includeImage: true,
          withFeatured: true,
        },
        {
          params: {
            frameName: FRAME_NAMES.DASHBOARD.DASHBOARD_INFO,
          },
        },
      );

      const dashboardInfo = data?.data?.at(0);

      if (dashboardInfo) {
        setMonitoringDashboard(convertToDashboardData(dashboardInfo));
      }
    } catch (e) {
      console.log(e);
    }
  };

  const convertingGridLayoutOption = (dashboardData: WidgetV8MappingResponseItem) => {
    const currentGridLayoutOption = monitoringDashboard.value.dashboardOption.gridLayoutOption;

    if (currentGridLayoutOption) {
      return dashboardData;
    }

    const convertedWidgets = convertDashboardWidgetsCoordinates(dashboardData);

    return convertedWidgets;
  };

  const fetchWidgetsForMonitoringDashboard = async (dashboardId: number) => {
    try {
      const { data } = await getV8DashboardsWidgets(
        { dashboardId },
        {
          params: {
            frameName: FRAME_NAMES.DASHBOARD.DASHBOARD_MAPPING_INFO,
          },
        },
      );
      widgetsForMonitoringDashboard.value = convertingGridLayoutOption(data.data?.[0] ?? {});
    } catch (e) {
      console.log(e);
      widgetsForMonitoringDashboard.value = {};
    }
  };

  const saveVisitLog = (dashboardId: number) => {
    if (!dashboardId || dashboardId < 0) {
      return;
    }
    try {
      postV8DashboardsDashboardVisitByDashboardId(dashboardId, {
        params: {
          frameName: FRAME_NAMES.DASHBOARD.VISIT_LOG,
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  const fetchMonitoringDashboard = async (dashboardId: number) => {
    await fetchCurrentDashboardInfo(dashboardId);
    await fetchWidgetsForMonitoringDashboard(dashboardId);
    saveVisitLog(dashboardId);
  };

  /**
   * 대시보드 ID만 설정 (전체 데이터 fetch 없이)
   * window popup 등에서 dashboardId만 필요한 경우 사용
   */
  const setMonitoringDashboardId = (dashboardId: number) => {
    monitoringDashboard.value.dashboardId = dashboardId;
  };

  return {
    monitoringDashboard,
    isVariableFilterDisabled,
    widgetsForMonitoringDashboard,
    fetchWidgetsForMonitoringDashboard,
    setMonitoringDashboard,
    setMonitoringDashboardId,
    fetchMonitoringDashboard,

    toggleShowTimePeriod,
    setGlobalPeriodRange,
    setGlobalDefaultPeriodRange,
    globalPeriodInterval,
    setGlobalPeriodInterval,

    globalTickSetting,
    setGlobalTickSetting,

    globalAlertActivation,
    setGlobalAlertActivation,
    globalOnlyTargetName,
    setGlobalOnlyTargetName,

    ...useControlDashboardApi(computed(() => monitoringDashboard.value.dashboardId)),
  };
});
